export const SUPPORT_VIEWS = ["documentation", "release-notes"] as const;

export type SupportView = (typeof SUPPORT_VIEWS)[number];


export interface MarkdownHeading {
    id: string;
    label: string;
    level: number;
}


interface ParsedMarkdownHeading extends MarkdownHeading {
    line: number;
}


export interface SearchMatch {
    start: number;
    end: number;
}


export type SupportLink =
    | { kind: "anchor"; href: string }
    | { kind: "external"; href: string }
    | { kind: "blocked" };


const ATX_HEADING = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const FENCE_START = /^\s{0,3}(`{3,}|~{3,})/;
const RELEASE_HEADING = /^(?:Unreleased|v\d+\.\d+\.\d+(?:\s+\(\d{4}-\d{2}-\d{2}\))?)$/i;


export function supportViewFromSearch(search: string): SupportView | null {
    const view = new URLSearchParams(search).get("view");

    return SUPPORT_VIEWS.find((candidate) => candidate === view) ?? null;
}


export function markdownInlineText(markdown: string): string {
    return markdown
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/<[^>]+>/g, "")
        .replace(/[`*_~]/g, "")
        .replace(/\\([\\`*{}\[\]()#+\-.!_>])/g, "$1")
        .trim();
}


export function createMarkdownSlugger(): (label: string) => string {
    const occurrences = new Map<string, number>();

    return (label: string) => {
        const base = markdownInlineText(label)
            .normalize("NFKC")
            .toLocaleLowerCase("en")
            .replace(/[^\p{L}\p{N}\s_-]/gu, "")
            .trim()
            .replace(/\s+/g, "-") || "section";
        const occurrence = occurrences.get(base) ?? 0;
        occurrences.set(base, occurrence + 1);

        return occurrence === 0 ? base : `${base}-${occurrence}`;
    };
}


function parseMarkdownHeadings(markdown: string): readonly ParsedMarkdownHeading[] {
    const slug = createMarkdownSlugger();
    const headings: ParsedMarkdownHeading[] = [];
    let fence: { marker: "`" | "~"; length: number } | null = null;

    for (const [index, line] of markdown.split(/\r?\n/).entries()) {
        const fenceMatch = line.match(FENCE_START);

        if (fenceMatch?.[1]) {
            const marker = fenceMatch[1][0] as "`" | "~";

            if (!fence) {
                fence = { marker, length: fenceMatch[1].length };
            }
            else if (fence.marker === marker && fenceMatch[1].length >= fence.length) {
                fence = null;
            }

            continue;
        }

        if (fence) {
            continue;
        }

        const match = line.match(ATX_HEADING);

        if (!match?.[1] || match[2] === undefined) {
            continue;
        }
        const level = match[1].length;
        const label = markdownInlineText(match[2]);
        const id = slug(label);
        headings.push({ id, label, level, line: index + 1 });
    }

    return headings;
}


export function markdownHeadingIdsByLine(markdown: string): ReadonlyMap<number, string> {
    return new Map(parseMarkdownHeadings(markdown).map((heading) => [heading.line, heading.id]));
}


export function extractMarkdownHeadings(markdown: string, view: SupportView): readonly MarkdownHeading[] {
    return parseMarkdownHeadings(markdown)
        .filter((heading) =>
            view === "documentation" ?
                heading.level <= 3
                :
                heading.level === 2 && RELEASE_HEADING.test(heading.label),
        )
        .map(({ id, label, level }) => ({ id, label, level }));
}


export function findTextMatches(text: string, query: string): readonly SearchMatch[] {
    const needle = query.trim().toLocaleLowerCase("en");

    if (!needle) {
        return [];
    }
    const haystack = text.toLocaleLowerCase("en");
    const matches: SearchMatch[] = [];
    let offset = 0;

    while (offset <= haystack.length - needle.length) {
        const start = haystack.indexOf(needle, offset);

        if (start === -1) {
            break;
        }
        matches.push({ start, end: start + needle.length });
        offset = start + needle.length;
    }

    return matches;
}


export function classifySupportLink(href: string | undefined): SupportLink {
    if (!href) {
        return { kind: "blocked" };
    }
    if (/^#[^\s]+$/.test(href)) {
        return { kind: "anchor", href };
    }

    try {
        const url = new URL(href);

        return url.protocol === "https:" && !url.username && !url.password ?
                { kind: "external", href: url.href }
            :
                { kind: "blocked" };
    } catch {
        return { kind: "blocked" };
    }
}


export function resolveScreenshotSource(
    source: string | undefined,
    screenshots: Readonly<Record<string, string>>,
): string | undefined {
    if (!source) {
        return undefined;
    }
    const cleanSource = source.split(/[?#]/, 1)[0]?.replaceAll("\\", "/");
    const fileName = cleanSource?.split("/").filter(Boolean).at(-1);

    if (!fileName || !/\.png$/i.test(fileName)) {
        return undefined;
    }

    return screenshots[fileName];
}
